import * as React from 'react';
import { PedigreesTableProps } from "./pedigrees-table.types";
import { NewTableColumn, NewTableColumnAction, NewTableColumnPredefinedActionType, NewTableHeaderAction, NewTableHeaderPredefinedActionType } from '@common/components/new-table';
import { PedigreeListDto } from '@common/dto/pedigree/list-dto';
import { I18n } from 'react-redux-i18n';
import ActionsButton from '@common/components/ActionsButton';
import { FolderPrimaryListDto } from '@common/dto/folder/primary-list-dto';
import { InfoMessage } from '@common/components/info-message';
import { HttpErrorDto } from '@common/dto/http';
import { ItemSecurityManagementOption } from '@common/components/item-security-management';
import { useAdminFunctionsController } from '@modules/pedigrees/controller/admin-functions';
import { AdminSmartListFunctionType } from '@common/dto/admin-function/type';
import { downloadFile } from '@common/utils/file';
import { BodyNameCell } from '@common/components/body-name-cell';
import { isSmartListDateFieldType, renderSmartListDateCell } from '@common/utils/smart-list-table-cells';
import { useTableController } from '@common/hooks/table';

export const useController = (props: PedigreesTableProps) => {
  const { 
    openPedigree,
    selectedFolderId, 
    adminFunctions, 
    filters, 
    columns, 
    selectedItems, 
    getData: getDataBase,
    onDeletePedigrees, 
    getColumnId, 
    showInfoMessage, 
    showMessage, 
    showErrorMessage, 
    closeMessage, 
    onCreateShortcut,
    onRenamePedigree, 
    onCopyPedigree,
    onMovePedigree,
  } = props;

  const {
    setChangingColumns,
    changingColumns,
    handleColumnAdd,
    handleColumnRemove,
    handleGetFields,
    handleSetCurrentSelectedRow,
    handleColumnsResizing,
    handleColumnsOrderChange
  } = useTableController(props, { withFirstActionsColumn: true });

  const {
    handleRestoreColumnsToDefault,
    handleSetDefaultColumnsForAllUsers,
    handleApplyCurrentColumnsToAllUsers,
    handleSetDefaultColumnsForSpecificUsers,
    handleApplyCurrentColumnsToSpecificUsers,
    getProfiles,
    handleProfileSelectorSubmit,
    closeProfileSelector,
    profileSelectorOpened,
  } = useAdminFunctionsController(props, { columns, setChangingColumns });

  const [copyPedigreeProps, setCopyPedigreeProps] = React.useState<{ id: PedigreeListDto['id'] } | undefined>();

  const handleOpenCopyPedigree = React.useCallback((id: PedigreeListDto['id']) => setCopyPedigreeProps({ id }), []);
  const closeCopyPedigree = React.useCallback(() => setCopyPedigreeProps(undefined), []);
  const handleCopyPedigree = async (folder: FolderPrimaryListDto, pedigreeName: string) => {
    if (!copyPedigreeProps) {
      return;
    }
    
    await onCopyPedigree({
      familyId: copyPedigreeProps.id,
      folderId: folder.id,
      pedigreeName,
    });
    closeCopyPedigree();
    showInfoMessage(I18n.t('Pedigree successfully copied'));
  };

  const [movePedigreeProps, setMovePedigreeProps] = React.useState<{ id: PedigreeListDto['id']; pedigreeName: string } | undefined>();

  const handleOpenMovePedigree = React.useCallback((id: PedigreeListDto['id'], data: PedigreeListDto) => setMovePedigreeProps({ id, pedigreeName: data.name }), []);
  const closeMovePedigree = React.useCallback(() => setMovePedigreeProps(undefined), []);
  const handleMovePedigree = async (folder: FolderPrimaryListDto) => {
    if (!movePedigreeProps && !selectedItems.length) {
      return;
    }

    const idsToSend = selectedItems.length > 1 ? selectedItems.map((x) => x.id) : [movePedigreeProps!.id];
    
    await onMovePedigree({ ids: idsToSend, folderId: folder.id });
    
    closeMovePedigree();
    showInfoMessage(selectedItems.length > 1 ? I18n.t('Pedigrees successfully moved') : I18n.t('Pedigree successfully moved'));
  };

  const [createShortcutProps, setCreateShortcutProps] = React.useState<{ id: PedigreeListDto['id']; pedigreeName: string } | undefined>();

  const handleOpenCreateShortcut = React.useCallback((id: PedigreeListDto['id'], data: PedigreeListDto) => setCreateShortcutProps({ id, pedigreeName: data.name }), []);
  const closeCreateShortcut = React.useCallback(() => setCreateShortcutProps(undefined), []);
  const handleCreateShortcut = async (folder: FolderPrimaryListDto, pedigreeName: string, openOnSave: boolean) => {
    if (!createShortcutProps) {
      return;
    }
    
    const shortcutId = await onCreateShortcut({
      folderId: folder.id,
      familyId: createShortcutProps.id,
      shortcutName: pedigreeName,
    });

    if (openOnSave) {
      openPedigree(createShortcutProps.id, {
        id: createShortcutProps.id,
        parentId: '0',
        name: pedigreeName,
        img: 'gif/pedigree_short.gif',
        shortcut: 'true',
        shortcutId: String(shortcutId),
      });
    } else {
      showInfoMessage(I18n.t('Shortcut created successfully'));
    }

    closeCreateShortcut();
  };

  const [renamePedigreeProps, setRenamePedigreeProps] = React.useState<{ id: PedigreeListDto['id']; pedigreeName: string } | undefined>();

  const handleOpenRenamePedigree = React.useCallback((id: PedigreeListDto['id'], data: PedigreeListDto) => setRenamePedigreeProps({ id, pedigreeName: data.name }), []);
  const closeRenamePedigree = React.useCallback(() => setRenamePedigreeProps(undefined), []);
  const handleRenamePedigree = async (params: { name: string; renameIndividuals: boolean }) => {
    if (!renamePedigreeProps) {
      return;
    }

    await onRenamePedigree({
      familyId: renamePedigreeProps.id,
      pedigreeName: params.name,
      renameIndividuals: params.renameIndividuals,
    });
    closeRenamePedigree();
    showInfoMessage(I18n.t('Pedigree renamed successfully'));
  };

  const handlePedigreeDeleteClick = (id: PedigreeListDto['id'], pedigree: PedigreeListDto) => {
    showMessage({
      header: I18n.t('Confirmation Message'),
      confirmText: I18n.t('Yes'),
      cancelText: I18n.t('No'),
      content: (
        <InfoMessage>
          {selectedItems.length > 1 
            ? I18n.t('Are you sure you wish to delete the selected pedigrees?')
            : I18n.t('Are you sure you wish to delete the selected pedigree?')}
        </InfoMessage>
      ),
      cancelBtnId: 'btn-message-box-dont-cancel',
      confirmBtnId: 'btn-message-box-ok',
      onCustomOk: async () => {
        try {
          const pedigreesToSend = selectedItems.length > 1 ? selectedItems : [pedigree];

          await onDeletePedigrees(pedigreesToSend);
          closeMessage();
        } catch (err) {
          console.error(err);
          showErrorMessage((err as HttpErrorDto)?.entity || I18n.t('Failed to delete pedigree'));
        }
      }
    });
  }

  const [generateExternalLinkOpened, setGenerateExternalLinkOpened] = React.useState(false);
  const selectedPedigreeId = React.useMemo(() => selectedItems.length === 1 ? selectedItems[0]?.id as PedigreeListDto['id'] : undefined, [selectedItems]);
  
  const closeGenerateExternalLink = React.useCallback(() => {
    setGenerateExternalLinkOpened(false);
  }, []);

  const openGenerateExternalLink = React.useCallback(() => {
    setGenerateExternalLinkOpened(true);
  }, []);

  const [pedigreeSecurityOpened, setPedigreeSecurityOpened] = React.useState(false);

  const closePedigreeSecurity = React.useCallback(() => {
    setPedigreeSecurityOpened(false);
  }, []);

  const openPedigreeSecurity = React.useCallback(() => {
    setPedigreeSecurityOpened(true);
  }, []);

  const getPedigreeSecurity = React.useCallback(() => {
    return props.getPedigreeSecurity(selectedPedigreeId!);
  }, [props.getPedigreeSecurity, selectedPedigreeId]);


  const updatePedigreeSecurity = async (securityOptions: Array<ItemSecurityManagementOption>) => {
    await props.updatePedigreeSecurity(securityOptions, selectedPedigreeId!);
    closePedigreeSecurity();
    props.showInfoMessage(I18n.t('Security settings were successfully changed'));
  };

  const generateExternalLinkMessage = React.useMemo(() => ({
    header: I18n.t('External URL'),
    confirmText: I18n.t('OK'),
    size: 'medium',
    hideMessageBox: closeGenerateExternalLink,
    onCustomOk: closeGenerateExternalLink,
  }), [closeGenerateExternalLink]);

  const handleExportPedigree = React.useCallback(async (predigreeId: PedigreeListDto['id'], pedigree: PedigreeListDto) => {
    try {
      const entity = await props.exportPedigree({ id: predigreeId });
      const filename = `${pedigree.name}.xml`;
      downloadFile(entity, filename);
    } catch (err) {
      console.error(err);
      showErrorMessage((err as HttpErrorDto)?.entity || I18n.t('Failed to export Pedigree'));
    }
  }, [props.exportPedigree, showErrorMessage]);

  const handleExportPedigrees = React.useCallback(() => {
    selectedItems.forEach(pedigree => {
      handleExportPedigree(pedigree.id, pedigree);
    });
  }, [selectedItems, handleExportPedigree]);
  
  const getActions = (original: PedigreeListDto) => {
    if (selectedItems.length > 1) {
      return [
        { label: 'Move Pedigrees', onClick: handleOpenMovePedigree },
        { label: 'Delete Pedigrees', onClick: handlePedigreeDeleteClick },
        { label: 'Export Pedigrees', onClick: handleExportPedigrees },
      ].map((x) => ({ ...x, label: I18n.t(x.label) }));
    }

    return [
      { label: 'Open Pedigree', onClick: openPedigree }, 
      { label: 'Copy Pedigree', onClick: handleOpenCopyPedigree, condition: Number(original.parentId) === 0 },
      { label: 'Move Pedigree', onClick: handleOpenMovePedigree, condition: original.modifyAllowed == 'true' },
      { label: 'Rename Pedigree', onClick: handleOpenRenamePedigree, condition: original.modifyAllowed == 'true' },
      { label: 'Delete Pedigree', onClick: handlePedigreeDeleteClick, condition: original.deleteAllowed == 'true' },
      { label: 'Open Pedigree Datasheet', onClick: props.openPedigreeDatasheet },
      { label: 'Create Shortcut', onClick: handleOpenCreateShortcut, condition: original.shortcut !== 'true' },
      { label: 'Export Pedigree', onClick: handleExportPedigree },
      { label: 'Edit Pedigree Security', onClick: openPedigreeSecurity },
      { label: 'Generate External Link', onClick: openGenerateExternalLink, condition: Number(original.parentId) === 0 }
    ].filter((x) => x.condition ?? true).map((x) => ({ ...x, label: I18n.t(x.label) }));
  };

  const renderActionsButton = React.useCallback(({ row: { original } }: { row: { original: PedigreeListDto } }) => {
    return (
      <ActionsButton
        id={original.id} 
        data={original} 
        stopPropagationOnRoot
        menuItems={getActions(original)}
        setCurrent={handleSetCurrentSelectedRow}
      />
    );
  }, [getActions, handleSetCurrentSelectedRow]);

  const renderPedigreeNameBodyCell = React.useCallback(({ row: { original } }: { row: { original: PedigreeListDto } }) => {
    return (
      <BodyNameCell
        item={original}
        label={original.name}
        onClick={() => openPedigree(original.id, original)}
      />
    );
  }, [openPedigree]);

  const tableColumns = React.useMemo((): Array<NewTableColumn<PedigreeListDto>> => {
    const defaultWidth = 100;
    const nameCellId = 16;

    return [
      { 
        disableActions: true,
        enableResizing: false,
        enableSorting: false,
        header: I18n.t('Actions'), 
        accessorKey: 'ACTIONS_COLUMNS_LOCAL',
        id: 'ACTIONS_COLUMNS_LOCAL',
        cell: renderActionsButton,
        minSize: 65,
        size: 65,
        className: 'table-actions-cell'
      },
      ...columns.map((x) => ({
        id: getColumnId(x),
        accessorKey: x.field.id.toString(),
        cell: x.field.id === nameCellId
          ? renderPedigreeNameBodyCell
          : isSmartListDateFieldType(x.field.type)
            ? renderSmartListDateCell
            : undefined,
        enableDragAndDrop: true,
        enableSorting: true,
        minSize: 20,
        size: x.width || defaultWidth,
        header: x.field.name,
        filter: { 
          type: x.field.type,
          options: x.field.options,
          outputDateFormat: 'YYYY-MM-DD'
        }
      }))
    ];
  }, [columns, getColumnId, renderActionsButton, renderPedigreeNameBodyCell]);


  type TableAction<T> = T & {
    condition?: boolean; 
    adminFunction?: AdminSmartListFunctionType;
  };

  const getColumnActions = () => {
    const baseList: Array<TableAction<NewTableColumnAction>> = [
      { type: NewTableColumnPredefinedActionType.filters, condition: Boolean(filters) },
      { adminFunction: AdminSmartListFunctionType.setDefaultForAllUsers, label: I18n.t('Set as Default for All Users'), className: 'PedigreesTable__column-action-top-divider', onClick: handleSetDefaultColumnsForAllUsers },
      { adminFunction: AdminSmartListFunctionType.setDefaultForSpecificUsers, label: I18n.t('Set as Default for Specific Profiles'), onClick: handleSetDefaultColumnsForSpecificUsers },
      { adminFunction: AdminSmartListFunctionType.applyCurrentColumnsToAllUsers, label: I18n.t('Apply Current Columns Now to All Users'), onClick: handleApplyCurrentColumnsToAllUsers },
      { adminFunction: AdminSmartListFunctionType.applyCurrentColumnsToSpecificProfiles, label: I18n.t('Apply Current Columns Now to Specific Profiles'), onClick: handleApplyCurrentColumnsToSpecificUsers },
      { adminFunction: AdminSmartListFunctionType.restoreColumnsToDefault, label: I18n.t('Restore Columns to Default'), onClick: handleRestoreColumnsToDefault },
      { adminFunction: AdminSmartListFunctionType.addColumns, type: NewTableColumnPredefinedActionType.addColumn, className: 'PedigreesTable__column-action-top-divider' },
      { adminFunction: AdminSmartListFunctionType.deleteColumn, type: NewTableColumnPredefinedActionType.deleteColumn },
      { type: NewTableColumnPredefinedActionType.clearSorting },
    ];

    return baseList.map((x) => {
      if (x.adminFunction) {
        x.condition = adminFunctions ? Boolean(adminFunctions[x.adminFunction]) : false;
      }

      return x;
    })
    .filter((x) => x.condition ?? true);
  };

  const getHeaderActions = () => {
    const baseList: Array<TableAction<NewTableHeaderAction>> = [
      { adminFunction: AdminSmartListFunctionType.setDefaultForAllUsers, label: I18n.t('Set as Default for All Users'), onClick: handleSetDefaultColumnsForAllUsers },
      { adminFunction: AdminSmartListFunctionType.setDefaultForSpecificUsers, label: I18n.t('Set as Default for Specific Profiles'), onClick: handleSetDefaultColumnsForSpecificUsers },
      { adminFunction: AdminSmartListFunctionType.applyCurrentColumnsToAllUsers, label: I18n.t('Apply Current Columns Now to All Users'), onClick: handleApplyCurrentColumnsToAllUsers },
      { adminFunction: AdminSmartListFunctionType.applyCurrentColumnsToSpecificProfiles, label: I18n.t('Apply Current Columns Now to Specific Profiles'), onClick: handleApplyCurrentColumnsToSpecificUsers },
      { adminFunction: AdminSmartListFunctionType.restoreColumnsToDefault, label: I18n.t('Restore Columns to Default'), onClick: handleRestoreColumnsToDefault },
      { adminFunction: AdminSmartListFunctionType.addColumns, type: NewTableHeaderPredefinedActionType.addColumn, className: 'PedigreesTable__column-action-top-divider' },
    ]
    
    return baseList.map((x) => {
      if (x.adminFunction) {
        x.condition = adminFunctions ? Boolean(adminFunctions[x.adminFunction]) : false;
      }

      return x;
    })
    .filter((x) => x.condition ?? true);;
  };

  const headerActions = getHeaderActions();
  const columnActions = getColumnActions();

  const getHeaderElementId = React.useCallback(() => {
    return 'ped-home-tab-list-grid-thead-row0';
  }, []);

  const getHeaderCellElementId = React.useCallback((column: NewTableColumn<PedigreeListDto>, columnIndex: number) => {
    return `ped-home-tab-list-grid-thead-row0-cell${columnIndex}`;
  }, []);

  const getBodyRowElementId = React.useCallback((dataItem: PedigreeListDto, rowIndex: number) => {
    return `ped-home-tab-list-grid-tbody-row${rowIndex}`;
  }, []);

  const getBodyCellElementId = React.useCallback((column: NewTableColumn<PedigreeListDto>, columnIndex: number, dataItem: PedigreeListDto, rowIndex: number) => {
    return `ped-home-tab-list-grid-tbody-row${rowIndex}-cell${columnIndex}`;
  }, []);

  return {
    changingColumns,
    renamePedigreeProps,
    closeRenamePedigree,
    handleRenamePedigree,
    createShortcutProps,
    closeCreateShortcut,
    handleCreateShortcut,
    movePedigreeProps,
    closeMovePedigree,
    handleMovePedigree,
    handleCopyPedigree,
    copyPedigreeProps,
    closeCopyPedigree,
    handleColumnRemove,
    handleGetFields,
    handleColumnAdd,
    getProfiles,
    handleProfileSelectorSubmit,
    closeProfileSelector,
    profileSelectorOpened,
    tableColumns,
    headerActions,
    columnActions,
    getData: getDataBase,
    handleColumnsResizing,
    handleColumnsOrderChange,
    getHeaderElementId,
    getHeaderCellElementId,
    getBodyRowElementId,
    getBodyCellElementId,
    generateExternalLinkOpened,
    closeGenerateExternalLink,
    generateExternalLinkMessage,
    selectedPedigreeId,
    getPedigreeSecurity,
    updatePedigreeSecurity,
    closePedigreeSecurity,
    pedigreeSecurityOpened,
  };
};